import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { ChevronDown } from 'lucide-react';

const HeroSection = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    const renderer = new THREE.WebGLRenderer({
      canvas: canvasRef.current,
      alpha: true,
      antialias: true,
    });

    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(window.devicePixelRatio);

    const roomGroup = new THREE.Group();
    
    const roomGeometry = new THREE.BoxGeometry(12, 8, 10);
    const roomEdges = new THREE.EdgesGeometry(roomGeometry);
    const roomMaterial = new THREE.LineBasicMaterial({ 
      color: 0xFF8000, 
      transparent: true, 
      opacity: 0.6 
    });
    const roomWireframe = new THREE.LineSegments(roomEdges, roomMaterial);
    roomGroup.add(roomWireframe);

    const sofaGeometry = new THREE.BoxGeometry(4, 2, 1.5);
    const sofaEdges = new THREE.EdgesGeometry(sofaGeometry);
    const sofaMaterial = new THREE.LineBasicMaterial({ 
      color: 0xFF8000, 
      transparent: true, 
      opacity: 0.8 
    });
    const sofa = new THREE.LineSegments(sofaEdges, sofaMaterial);
    sofa.position.set(0, -2, -3);
    roomGroup.add(sofa);

    const tableGeometry = new THREE.BoxGeometry(2, 0.8, 1.2);
    const tableEdges = new THREE.EdgesGeometry(tableGeometry);
    const tableMaterial = new THREE.LineBasicMaterial({ 
      color: 0x2E4080, 
      transparent: true, 
      opacity: 0.7 
    });
    const table = new THREE.LineSegments(tableEdges, tableMaterial);
    table.position.set(0, -3, 0);
    roomGroup.add(table);

    const lampGeometry = new THREE.CylinderGeometry(0.3, 0.3, 2, 8);
    const lampEdges = new THREE.EdgesGeometry(lampGeometry);
    const lampMaterial = new THREE.LineBasicMaterial({ 
      color: 0xFFD700, 
      transparent: true, 
      opacity: 0.6 
    });
    const lamp = new THREE.LineSegments(lampEdges, lampMaterial);
    lamp.position.set(-4, -2, 3);
    roomGroup.add(lamp);

    const windowGeometry = new THREE.PlaneGeometry(3, 4);
    const windowEdges = new THREE.EdgesGeometry(windowGeometry);
    const windowMaterial = new THREE.LineBasicMaterial({ 
      color: 0x008080, 
      transparent: true, 
      opacity: 0.5 
    });
    const windowFrame = new THREE.LineSegments(windowEdges, windowMaterial);
    windowFrame.position.set(-5.9, 0, -2);
    windowFrame.rotation.y = Math.PI / 2;
    roomGroup.add(windowFrame);

    const particlesGeometry = new THREE.BufferGeometry();
    const particlesCount = 1000;
    const posArray = new Float32Array(particlesCount * 3);

    for (let i = 0; i < particlesCount * 3; i++) {
      posArray[i] = (Math.random() - 0.5) * 50;
    }

    particlesGeometry.setAttribute(
      'position',
      new THREE.BufferAttribute(posArray, 3)
    );

    const particlesMaterial = new THREE.PointsMaterial({
      size: 0.03,
      color: 0xFF8000,
      transparent: true,
      opacity: 0.4,
      blending: THREE.AdditiveBlending,
    });

    const particlesMesh = new THREE.Points(particlesGeometry, particlesMaterial);
    scene.add(particlesMesh);

    scene.add(roomGroup);
    camera.position.z = 20;
    camera.position.y = 2;

    let mouseX = 0;
    let mouseY = 0;

    const handleMouseMove = (event) => {
      mouseX = (event.clientX / window.innerWidth) * 2 - 1;
      mouseY = -(event.clientY / window.innerHeight) * 2 + 1;
    };

    window.addEventListener('mousemove', handleMouseMove);

    const animate = () => {
      requestAnimationFrame(animate);

      roomGroup.rotation.y += 0.002;
      roomGroup.rotation.x = Math.sin(Date.now() * 0.0005) * 0.1;

      particlesMesh.rotation.y += 0.0005;

      camera.position.x += (mouseX * 3 - camera.position.x) * 0.03;
      camera.position.y += (mouseY * 2 + 2 - camera.position.y) * 0.03;
      camera.lookAt(scene.position);

      renderer.render(scene, camera);
    };

    animate();

    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', handleResize);
      renderer.dispose();
    };
  }, []);

  return (
    <section className="relative h-screen w-full overflow-hidden" style={{
      background: 'linear-gradient(135deg, #FFF5E6 0%, #FFE8CC 30%, #E6F2FF 70%, #FFFFFF 100%)'
    }}>
      {/* Three.js Canvas */}
      <canvas
        ref={canvasRef}
        className="absolute top-0 left-0 w-full h-full"
        style={{ opacity: 0.5 }}
      />

      {/* Decorative Blobs */}
      <div className="absolute top-20 right-20 w-96 h-96 bg-gradient-to-br from-orange-300/30 to-yellow-300/30 blob float-animation" style={{ animationDelay: '0s' }} />
      <div className="absolute bottom-20 left-20 w-80 h-80 bg-gradient-to-br from-blue-300/30 to-teal-300/30 blob-2 float-animation" style={{ animationDelay: '2s' }} />

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center px-6 md:px-[7.6923%] text-center">
        <div className="max-w-5xl space-y-8">
          {/* Main headline with gradient */}
          <h1
            className="text-5xl md:text-7xl lg:text-8xl font-bold leading-tight"
            style={{
              background: 'linear-gradient(135deg, #FF8000 0%, #FFD700 50%, #2E4080 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '-0.02em',
            }}
          >
            Your Dream Home, Your Way
          </h1>

          {/* Subheadline */}
          <p
            className="text-xl md:text-2xl lg:text-3xl font-medium max-w-4xl mx-auto text-[#4A4A4A]"
            style={{
              lineHeight: '1.6',
            }}
          >
            See it in AR. Design it yourself. Order everything directly. No contractors needed — just your dream space, brought to life.
          </p>

          {/* CTA Buttons with creative effects */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-6">
            <a href="#register">
              <button 
                className="group relative bg-gradient-to-r from-orange-500 to-orange-600 text-white border-none rounded-2xl px-10 py-5 text-lg font-semibold cursor-pointer transition-all duration-400 hover:shadow-2xl hover:scale-105 min-h-[60px] flex items-center justify-center gap-3 w-full sm:w-auto overflow-hidden"
              >
                <span className="relative z-10">Register Your Interest</span>
                <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-yellow-400 opacity-0 group-hover:opacity-100 transition-opacity duration-400" />
              </button>
            </a>
            <a href="#how-it-works">
              <button className="glass text-[#FF8000] border-2 border-orange-400 rounded-2xl px-10 py-5 text-lg font-semibold cursor-pointer transition-all duration-400 hover:bg-orange-50 hover:shadow-xl hover:scale-105 min-h-[60px] flex items-center justify-center gap-3 w-full sm:w-auto">
                See How It Works
              </button>
            </a>
          </div>

          {/* Trust indicators */}
          <div className="flex flex-wrap justify-center gap-6 pt-8 text-sm text-[#808080]">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span>AR-Powered</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
              <span>AI-Assisted</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
              <span>Direct Marketplace</span>
            </div>
          </div>
        </div>

        {/* Scroll indicator with animation */}
        <a
          href="#what-is"
          className="absolute bottom-12 left-1/2 transform -translate-x-1/2 animate-bounce"
        >
          <div className="flex flex-col items-center gap-2">
            <ChevronDown size={40} color="#FF8000" strokeWidth={2.5} />
            <span className="text-xs font-medium text-[#FF8000]">Scroll to explore</span>
          </div>
        </a>
      </div>
    </section>
  );
};

export default HeroSection;
